import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";
import { QK } from "../../../../../../base/qk";
import {
  getAssessmentByID,
  updateAssessment,
} from "../../../../../../api/services/observations";
import CustomInput from "../../../../../CustomForm/CustomInput";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import CustomDateField from "../../../../../FormFields/CustomDateField";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";
import CommonButton from "../../../../../CustomForm/CommonButton";

const EditPulseModal = ({ open, onClose, selectedID }) => {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    value: "",
    notes: "",
    date_time: "",
    handover: false,
    manager: false,
  });

  // Fetch entry by ID
  const { data: assessment } = useQuery(
    [selectedID],
    () => getAssessmentByID(selectedID),
    {
      enabled: !!selectedID,
      onSuccess: (data) => {
        setFormData({
          value: data.value || "",
          notes: data.notes || "",
          date_time: data.date_time || "",
          handover: data.handover || false,
          manager: data.manager || false,
        });
      },
    }
  );

  // Update mutation
  const { mutate: updateAssessmentMutation } = useMutation(
    (newData) => updateAssessment(selectedID, newData),
    {
      onSuccess: () => {
        enqueueSnackbar("Blood Sugar data updated successfully", {
          variant: "success",
        });
        queryClient.invalidateQueries([QK.pulse_rates]);
        onClose();
      },
      onError: (error) => {
        console.error("Error updating Oxygen Saturations data", error);
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckBox = (name, checked) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    updateAssessmentMutation(formData);
  };

  if (!assessment) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Edit Blood Sugar Entry
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <CustomInput
                label="Pulse Rate*"
                name="value"
                type="text"
                value={formData.value}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <CustomTextArea
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <CustomDateField
                name="date_time"
                label="Date / Time"
                type="datetime-local"
                value={formData.date_time}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <CustomCheckbox
                label="Share with Manager"
                name="manager"
                isInitiallyChecked={formData.manager}
                onClick={(checked) => handleCheckBox("manager", checked)}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <CustomCheckbox
                label="Handover"
                name="handover"
                isInitiallyChecked={formData.handover}
                onClick={(checked) => handleCheckBox("handover", checked)}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          {/* Option A: use MUI buttons directly */}
          <Button variant="outlined" color="primary" onClick={onClose}>
            Close
          </Button>
          <Button variant="contained" color="primary" type="submit">
            Save
          </Button>

          {/* Option B: keep CommonButton if it accepts MUI props
          <CommonButton onSubmit={handleSubmit} onClose={onClose} />
          */}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default EditPulseModal;